import {
  type ChangeEvent,
  type ForwardedRef,
  forwardRef,
  useEffect,
  useRef,
} from 'react'

import { useIgnoreWheelEvents } from './hooks'
import type { InputProps } from './types'

const invalidPatternMessage = 'Must be a whole number.'
const numberRegEx = /^-?\d*$/

const NumberInput = forwardRef(
  (
    { error, name, onChange, ...rest }: InputProps,
    ref?: ForwardedRef<HTMLInputElement | null>,
  ) => {
    const localRef = useRef<HTMLInputElement | null>(null)
    useIgnoreWheelEvents(localRef)

    useEffect(() => {
      if (!error) return localRef.current?.setCustomValidity('')
      localRef.current?.setCustomValidity(error)
      localRef.current?.reportValidity()
    }, [error])

    // Trim space and check validity
    function localOnChange(e: ChangeEvent<HTMLInputElement>) {
      e.target.value = e.target.value.trim()
      if (numberRegEx.test(e.target.value)) e.target.setCustomValidity('')
      else e.target.setCustomValidity(invalidPatternMessage)
      onChange && onChange(e)
    }

    function setRef(element: HTMLInputElement | null) {
      localRef.current = element
      if (!ref) return
      if (typeof ref === 'function') ref(element)
      else ref.current = element
    }

    return (
      <input
        autoComplete="off"
        data-lpignore
        id={name}
        name={name}
        onChange={localOnChange}
        ref={setRef}
        step={1}
        type="number"
        {...rest}
      />
    )
  },
)

export default NumberInput
